/**
 * snapshotStore — the last good answer to every GET, kept in IndexedDB.
 *
 * This is what lets every screen keep working with no connection: when a
 * read fails for lack of network, ui/api.js answers it from here instead,
 * and the page renders the data it had the last time it was online (the
 * offline banner says how old). It is a fallback, never a shortcut: while
 * the server is reachable every read still goes to the server.
 *
 * Why IndexedDB and not the cacheStore's localStorage: the student
 * directory alone is ~1MB and localStorage tops out around 5MB for the
 * whole origin; IndexedDB has room for all of it.
 *
 * Personal data on disk: entries are keyed by user id and the whole store
 * is wiped on every login and logout (app/auth.js), so on a shared office
 * machine the next person never sees the previous person's rows.
 *
 * Memory mirror: every entry read or written this session is also held in
 * a Map, so a second offline read of the same thing needs no disk access.
 */

const DB_NAME = 'mtz-offline';
const STORE = 'snapshots';

let dbPromise = null;
const memory = new Map();

function openDb() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      // Private mode / blocked storage: run on the memory mirror alone.
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    });
  }
  return dbPromise;
}

function tx(mode, fn) {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        t.oncomplete = () => resolve(req?.result ?? null);
        t.onerror = () => resolve(null);
        t.onabort = () => resolve(null);
      } catch { resolve(null); }
    });
  });
}

/** Save the answer to a read. Fire-and-forget; never throws. */
export function putSnapshot(key, data) {
  const entry = { data, at: Date.now() };
  memory.set(key, entry);
  tx('readwrite', (s) => s.put(entry, key));
}

/** The last saved answer, as { data, at }, or null. */
export async function getSnapshot(key) {
  if (memory.has(key)) return memory.get(key);
  const entry = await tx('readonly', (s) => s.get(key));
  if (entry) memory.set(key, entry);
  return entry || null;
}

/** Forget everything — on login and logout. */
export function clearSnapshots() {
  memory.clear();
  return tx('readwrite', (s) => s.clear());
}
